import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FlaskConical, Printer, Mail, Edit2, X, ChevronDown, Eye, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import {
  TAMANO_INFO, DISENO_INFO, getDimensiones,
  type TemplateData,
} from '../configuracion/templates/template-types';
import { TemplateRenderer } from '../configuracion/templates/TemplateRenderer';
import { usePlantillasConfig, DEFAULT_PLANTILLAS_CONFIG } from '../configuracion/templates/usePlantillaConfig';
import { EstudioWizard } from './EstudioWizard';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';

// ─── Types ───────────────────────────────────────────────────────────────────

type SolicitudStatus = 'borrador' | 'activa' | 'cancelada';

const STATUS_INFO: Record<SolicitudStatus, { label: string; cls: string }> = {
  borrador:  { label: 'Borrador',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  activa:    { label: 'Activa',    cls: 'bg-green-50 text-green-700 border-green-200' },
  cancelada: { label: 'Cancelada', cls: 'bg-red-50 text-red-500 border-red-200' },
};

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Print Modal ─────────────────────────────────────────────────────────────

function EstudioPrintModal({ solicitud, paciente, clinica, usuario, onClose }: {
  solicitud: any; paciente: any; clinica: any; usuario: any; onClose: () => void;
}) {
  const printRef = useRef<HTMLDivElement>(null);
  const { data: plantillasConfig } = usePlantillasConfig();
  const cfg = (plantillasConfig ?? DEFAULT_PLANTILLAS_CONFIG).laboratorio;
  const { width: pw, height: ph, cssSize } = getDimensiones(cfg.tamanoHoja, cfg.orientacion);
  const SCALE = 0.72;
  const modalW = Math.round(pw * SCALE);
  const modalH = Math.round(ph * SCALE);

  const nombre = [paciente?.nombre, paciente?.apellidoPaterno, paciente?.apellidoMaterno].filter(Boolean).join(' ');

  const data: TemplateData = {
    tipo: 'laboratorio',
    folio: solicitud.folio || '—',
    fecha: formatDate(solicitud.fecha || new Date()),
    qrUrl: solicitud.folio ? `${window.location.origin}/estudio-digital/${solicitud.folio}` : undefined,
    generadoPor: solicitud.medico?.nombre || usuario?.nombre,
    clinica: {
      nombre:    clinica?.nombre    || 'Clínica',
      cedula:    clinica?.cedulaFiscal || '',
      direccion: clinica?.direccion || '',
      telefono:  clinica?.telefono  || '',
    },
    medico: {
      nombre:       solicitud.medico?.nombre || usuario?.nombre || 'Médico',
      cedula:       solicitud.medico?.cedula || '',
      especialidad: solicitud.medico?.especialidad || '',
    },
    paciente: {
      nombre,
      fechaNacimiento: paciente?.fechaNacimiento ? formatDate(paciente.fechaNacimiento) : '',
      sexo: paciente?.genero ? ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[paciente.genero as string] || '') : '',
    },
    fechaConsulta: formatDate(solicitud.fecha || new Date()),
    estudios: solicitud.estudios || [],
    instrucciones: solicitud.indicaciones || undefined,
  };

  function handlePrint() {
    const el = printRef.current;
    if (!el) return;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => w.print();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-5xl">
        <div className="flex items-center justify-between px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold text-sm text-text-primary">{solicitud.folio}</h2>
            <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border',
              STATUS_INFO[(solicitud.status || 'activa') as SolicitudStatus]?.cls
            )}>{STATUS_INFO[(solicitud.status || 'activa') as SolicitudStatus]?.label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{TAMANO_INFO[cfg.tamanoHoja].label}</span>
            <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">{DISENO_INFO[cfg.diseno].label}</span>
          </div>
          <div className="flex items-center gap-2">
            {solicitud.status !== 'cancelada' && (
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
                <Printer size={12} /> Imprimir
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 flex justify-center overflow-x-auto">
          <div style={{ width: modalW, height: modalH, overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: 4 }}>
            <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
              <TemplateRenderer data={data} diseno={cfg.diseno} width={pw} height={ph} disenoPersonalizadoHtml={cfg.disenoPersonalizadoHtml} />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
          <p className="text-xs text-text-secondary">Plantilla configurada en Configuración → Plantillas</p>
          <button onClick={() => toast.info('Envío por correo electrónico próximamente')}
            className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            <Mail size={12} /> Enviar por correo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface Props {
  id: string;
  canEdit: boolean;
  paciente?: any;
  noDelete?: boolean;
  triggerAdd?: number;
}

export function EstudiosTab({ id, canEdit, paciente, noDelete, triggerAdd }: Props) {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const puedeEmitir = canEdit && moduloAplica('estudios_emitir', perfilTipo, modulosHabilitados);

  const [filterStatus, setFilterStatus] = useState<SolicitudStatus | ''>('');
  const [filterDesde,  setFilterDesde]  = useState('');
  const [filterHasta,  setFilterHasta]  = useState('');
  const [wizardOpen,   setWizardOpen]   = useState(false);
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [printSolicitud, setPrintSolicitud] = useState<any | null>(null);
  useEffect(() => { if (triggerAdd && puedeEmitir) setWizardOpen(true); }, [triggerAdd, puedeEmitir]);

  const { data: clinica } = useQuery({
    queryKey: ['clinica', usuario?.clinicaId],
    queryFn: () => api.get(`/api/clinicas/${usuario!.clinicaId}`).then(r => r.data),
    enabled: !!usuario?.clinicaId,
    staleTime: 300_000,
  });

  const { data: solicitudes = [], isLoading } = useQuery({
    queryKey: ['estudios-paciente', id, filterStatus, filterDesde, filterHasta],
    queryFn: () => api.get(`/api/pacientes/${id}/estudios`, {
      params: {
        status:     filterStatus || undefined,
        fechaDesde: filterDesde  || undefined,
        fechaHasta: filterHasta  || undefined,
      },
    }).then(r => r.data as any[]),
  });

  const cancelMutation = useMutation({
    mutationFn: (solicitudId: string) => api.patch(`/api/estudios/${solicitudId}`, { status: 'cancelada' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['estudios-paciente', id] });
      toast.success('Solicitud cancelada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cancelar')),
  });

  const activarMutation = useMutation({
    mutationFn: (solicitudId: string) => api.post(`/api/estudios/${solicitudId}/activar`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['estudios-paciente', id] });
      toast.success('Solicitud emitida');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al emitir la solicitud')),
  });

  function openWizard(solicitudId?: string) {
    setEditingId(solicitudId || null);
    setWizardOpen(true);
  }

  function closeWizard() {
    setWizardOpen(false);
    setEditingId(null);
    qc.invalidateQueries({ queryKey: ['estudios-paciente', id] });
  }

  const hasFilters = !!(filterStatus || filterDesde || filterHasta);

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">{solicitudes.length}</span> solicitud{solicitudes.length !== 1 ? 'es' : ''}
          </p>
          {puedeEmitir && (
            <button onClick={() => openWizard()}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
              <Plus size={13} /> Nueva solicitud
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as SolicitudStatus | '')}
              className="appearance-none border border-gray-200 rounded-lg pl-2.5 pr-6 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer">
              <option value="">Todos los estados</option>
              <option value="activa">Activa</option>
              <option value="borrador">Borrador</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
          </div>
          <input type="date" value={filterDesde} onChange={e => setFilterDesde(e.target.value)}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300" />
          <input type="date" value={filterHasta} onChange={e => setFilterHasta(e.target.value)}
            className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300" />
          {hasFilters && (
            <button onClick={() => { setFilterStatus(''); setFilterDesde(''); setFilterHasta(''); }}
              className="text-xs text-primary-600 hover:underline">Limpiar</button>
          )}
        </div>

        {/* List */}
        {isLoading ? (
          <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>
        ) : solicitudes.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <FlaskConical size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">
              {hasFilters ? 'Sin resultados para los filtros seleccionados' : 'Sin solicitudes de estudio registradas'}
            </p>
            {puedeEmitir && !hasFilters && (
              <button onClick={() => openWizard()}
                className="mt-3 text-xs text-primary-600 hover:underline">Crear primera solicitud</button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {solicitudes.map((s: any) => {
              const info = STATUS_INFO[(s.status || 'activa') as SolicitudStatus] || STATUS_INFO.activa;
              return (
                <div key={s.id} className="rounded-xl border border-gray-100 bg-white p-4 space-y-2.5">
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', info.cls)}>{info.label}</span>
                      <span className="text-sm font-semibold text-text-primary">{s.folio}</span>
                    </div>
                    <span className="text-xs text-text-secondary flex-shrink-0">{formatDate(s.fecha)}</span>
                  </div>

                  {/* Doctor */}
                  {s.medico && (
                    <p className="text-xs text-text-secondary">Dr. {s.medico.nombre}</p>
                  )}

                  {/* CIE-11 */}
                  {s.tituloCie11 && (
                    <p className="text-xs text-text-secondary">
                      <span className="font-mono text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mr-1.5">{s.codigoCie11}</span>
                      {s.tituloCie11}
                    </p>
                  )}

                  {/* Studies */}
                  {s.estudios?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(s.estudios as any[]).slice(0, 5).map((e: any, i: number) => (
                        <span key={i} className="bg-teal-50 text-teal-700 text-[10px] px-2 py-0.5 rounded-full border border-teal-100">
                          {e.nombre}
                        </span>
                      ))}
                      {s.estudios.length > 5 && (
                        <span className="text-[10px] text-text-secondary px-1">+{s.estudios.length - 5} más</span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-1 border-t border-gray-50">
                    {s.status === 'activa' && (
                      <>
                        <button onClick={() => setPrintSolicitud(s)}
                          className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                          <Printer size={11} /> Ver / Imprimir
                        </button>
                        <button onClick={() => toast.info('Envío por correo electrónico próximamente')}
                          className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                          <Mail size={11} /> Enviar
                        </button>
                      </>
                    )}
                    {s.status === 'cancelada' && (
                      <button onClick={() => setPrintSolicitud(s)}
                        className="flex items-center gap-1 text-[10.5px] text-text-secondary hover:text-text-primary transition-colors">
                        <Eye size={11} /> Ver
                      </button>
                    )}
                    {puedeEmitir && s.status === 'borrador' && (
                      <>
                        <button onClick={() => openWizard(s.id)}
                          className="flex items-center gap-1 text-[10.5px] text-primary-600 hover:text-primary-800 transition-colors">
                          <Edit2 size={11} /> Editar
                        </button>
                        <button
                          onClick={() => { if (confirm('¿Emitir esta solicitud ahora?')) activarMutation.mutate(s.id); }}
                          disabled={activarMutation.isPending}
                          className="flex items-center gap-1 text-[10.5px] text-green-600 hover:text-green-800 disabled:opacity-50 transition-colors">
                          <Send size={11} /> Emitir
                        </button>
                      </>
                    )}
                    {puedeEmitir && !noDelete && s.status !== 'cancelada' && (
                      <button
                        onClick={() => { if (confirm('¿Cancelar esta solicitud?')) cancelMutation.mutate(s.id); }}
                        className="flex items-center gap-1 text-[10.5px] text-red-400 hover:text-red-600 transition-colors ml-auto">
                        <X size={11} /> Cancelar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Wizard */}
      {wizardOpen && (
        <EstudioWizard
          pacienteId={id}
          solicitudId={editingId}
          paciente={paciente}
          onClose={closeWizard}
        />
      )}

      {/* Print modal */}
      {printSolicitud && (
        <EstudioPrintModal
          solicitud={printSolicitud}
          paciente={paciente}
          clinica={clinica}
          usuario={usuario}
          onClose={() => setPrintSolicitud(null)}
        />
      )}
    </>
  );
}
